const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_MODEL   = 'llama-3.3-70b-versatile';

// Canonical AEC vocabulary — anchors Groq's output to terms the app already uses
const AEC_STAGES = [
  'Concept Design', 'Schematic Design', 'Design Development',
  'Construction Documentation', 'Tender & Procurement',
  'Construction', 'Commissioning & Handover', 'Operations & Maintenance',
];

const AEC_ROLES = [
  'Architect', 'BIM Manager', 'Civil Engineer', 'Structural Engineer',
  'MEP Engineer', 'Project Manager', 'Quantity Surveyor', 'Contractor',
  'Facility Manager', 'Urban Planner', 'Interior Designer', 'Landscape Architect',
];

/**
 * Build a compact tool summary for the prompt.
 * Keeps token count low by only including populated fields.
 */
function buildToolBlock(tool, features, compact = false) {
  const maxFeats = compact ? 10 : 18;
  const maxDesc  = compact ? 350 : 600;

  const availableFeats = (features ?? [])
    .filter((f) => f.value_boolean === true || (f.value_text != null && f.value_text !== ''))
    .map((f) => f.feature_name + (f.value_text ? `: ${f.value_text}` : ''))
    .slice(0, maxFeats);

  const lines = [
    `SLUG: ${tool.slug}`,
    `Name: ${tool.name}${tool.vendor_name ? ` by ${tool.vendor_name}` : ''}`,
    tool.tagline              && `Tagline: ${tool.tagline}`,
    tool.description_long     && `Description: ${tool.description_long.slice(0, maxDesc)}`,
    !tool.description_long && tool.description_short && `Description: ${tool.description_short}`,
    tool.disciplines?.length  && `Disciplines: ${tool.disciplines.join(', ')}`,
    tool.platforms?.length    && `Platforms: ${tool.platforms.join(', ')}`,
    !compact && tool.integrations?.length && `Integrations: ${tool.integrations.slice(0, 10).join(', ')}`,
    tool.pricing_model        && `Pricing: ${tool.pricing_model}` +
      (tool.price_from ? `, from ${tool.price_currency ?? 'USD'} ${tool.price_from}/yr` : ''),
    availableFeats.length     && `Features: ${availableFeats.join('; ')}`,
  ].filter(Boolean);

  return lines.join('\n');
}

/**
 * Call Groq to generate a structured AI comparison of 2-3 AEC tools.
 *
 * @param {{ tools: object[], featuresMap: Object.<string, object[]> }} params
 * @returns {Promise<{
 *   tools: Array<{
 *     slug: string,
 *     popular_for: string,
 *     best_for: string,
 *     best_roles: string[],
 *     best_disciplines: string[],
 *     best_stages: string[],
 *     top_usecases: string[],
 *     top_features: Array<{ name: string, description: string }>
 *   }>,
 *   comparison: {
 *     key_differences: string[],
 *     recommendation: { individual: string, small_team: string, enterprise: string }
 *   }
 * }>}
 */
export async function generateToolComparison({ tools, featuresMap }) {
  const apiKey = process.env.REACT_APP_GROQ_API_KEY;
  if (!apiKey) throw new Error('REACT_APP_GROQ_API_KEY is not configured');
  if (tools.length < 2) throw new Error('Need at least 2 tools to compare');

  const compact = tools.length === 3;
  const toolBlocks = tools
    .map((t) => buildToolBlock(t, featuresMap[t.id], compact))
    .join('\n\n---\n\n');

  const systemPrompt =
    `You are an expert AEC (Architecture, Engineering and Construction) software analyst.
Produce a structured JSON comparison of the provided tools.
Rules:
- Base ALL claims strictly on the supplied data — do not invent features.
- Use ONLY canonical project stages: ${AEC_STAGES.join(', ')}.
- Use ONLY canonical roles: ${AEC_ROLES.join(', ')}.
- Return ONLY valid JSON — no markdown, no explanation, no code fences.`;

  const responseSchema = {
    tools: tools.map((t) => ({
      slug: t.slug,
      popular_for: 'One sentence — the primary use case this tool is known for in AEC',
      best_for: 'One sentence — ideal team size or project type',
      best_roles: ['Role 1', 'Role 2'],
      best_disciplines: ['Discipline 1'],
      best_stages: ['Stage 1', 'Stage 2'],
      top_usecases: ['Use case 1', 'Use case 2', 'Use case 3'],
      top_features: [
        { name: 'Feature name', description: 'One-line value description' },
      ],
    })),
    comparison: {
      key_differences: [
        'Concise difference 1',
        'Concise difference 2',
        'Concise difference 3',
      ],
      recommendation: {
        individual: 'ToolName: reason for solo / individual users',
        small_team:  'ToolName: reason for small teams (2–15 people)',
        enterprise:  'ToolName: reason for large organisations',
      },
    },
  };

  const userPrompt =
    `Compare these ${tools.length} AEC tools. Return JSON matching this schema exactly:\n` +
    JSON.stringify(responseSchema, null, 2) +
    `\n\nTool data:\n${toolBlocks}`;

  const res = await fetch(GROQ_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user',   content: userPrompt   },
      ],
      response_format: { type: 'json_object' },
      temperature: 0.2,
      max_tokens: tools.length === 3 ? 4000 : 2600,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error?.message ?? `Groq API ${res.status}`);
  }

  const data = await res.json();
  const raw  = data.choices?.[0]?.message?.content;
  if (!raw) throw new Error('Empty response from Groq');

  return JSON.parse(raw);
}
